import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getTesislerPage } from "../services/api";
import { useLang } from "../context/LanguageContext";
import ScrollReveal from "../components/ScrollReveal";
import defaultTesisler from '../default_datas/pages/defaultTesisler';

const STRAPI_BASE = import.meta.env.VITE_STRAPI_URL || "";

const getStrapiImg = (field) => {
  if (!field) return null;

  // Strapi v5 — düz obje: { url: "https://..." }
  if (field.url) {
    return field.url.startsWith("http") ? field.url : `${STRAPI_BASE}${field.url}`;
  }

  // Strapi v4 — { data: { attributes: { url } } }
  const single = field?.data?.attributes?.url;
  if (single) return single.startsWith("http") ? single : `${STRAPI_BASE}${single}`;

  // Strapi v4 array — { data: [{ attributes: { url } }] }
  const first = field?.data?.[0]?.attributes?.url;
  if (first) return first.startsWith("http") ? first : `${STRAPI_BASE}${first}`;

  return null;
};

const Tesisler = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [data, setData] = useState(defaultTesisler);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTesislerPage()
      .then((res) => {
        if (res && res.pageContent) {
          setData(res);
        }
      })
      .catch((err) => {
        console.error("❌ Strapi hatası - Default Tesisler verisi kullanılıyor:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const t = (section, field) => section?.[lang === "en" ? `${field}_en` : field] || section?.[field] || "";
  const ti = (item, field) => item?.[lang === "en" ? `${field}_en` : field] || item?.[field] || "";

  if (loading) return <div className="min-h-screen bg-ruzy-light animate-pulse" />;

  const pageContent = data?.pageContent || [];

  return (
    <div className="bg-ruzy-light font-poppins text-ruzy-dark min-h-screen">
      <main className="max-w-7xl mx-auto px-4 py-12">
        {pageContent.map((section, idx) => {

          // 1. HERO SECTION
          if (section.__component === "sections.hero") {
            const heroImg = getStrapiImg(section.image);
            return (
              <ScrollReveal key={idx}>
                <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
                  <div className="flex flex-col gap-6">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-ruzy-accent/10 text-ruzy-accent w-fit uppercase">
                      {t(section, "badge")}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-black text-ruzy-dark leading-tight">
                      {t(section, "title")}
                    </h1>
                    <p className="text-lg text-ruzy-text leading-relaxed">
                      {t(section, "description")}
                    </p>
                    <div className="flex gap-4 mt-4">
                      <button onClick={() => navigate("/iletisim")} className="h-14 px-8 bg-ruzy-accent text-white rounded-lg font-bold hover:shadow-lg transition-all">
                        {lang === "en" ? "Get Started" : "Hemen Başla"}
                      </button>
                      <button onClick={() => navigate("/iletisim")} className="h-14 px-8 bg-ruzy-accent text-white rounded-lg font-bold hover:shadow-lg transition-all">
                        {lang === "en" ? "Schedule a Demo Appointment" : "Demo Randevusu Al"}
                      </button>
                    </div>
                  </div>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
                    {heroImg && (
                      <img src={heroImg} alt="Tesis Güvenliği" className="w-full aspect-video object-cover" />
                    )}
                  </div>
                </section>
              </ScrollReveal>
            );
          }

          // 2. FEATURES SECTION
          if (section.__component === "sections.features-section") {
            return (
              <ScrollReveal key={idx} delay={0.1}>
                <section className="mb-24">
                  <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">{t(section, "Title")}</h2>
                    <p className="text-ruzy-text max-w-2xl mx-auto">{t(section, "description")}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {(section.cards || []).map((card, i) => (
                      <div key={i} className="p-8 bg-white rounded-2xl border border-ruzy-border hover:border-ruzy-accent transition-all group">
                        <span className="material-symbols-outlined text-4xl text-ruzy-accent mb-4">{card.icon}</span>
                        <h3 className="text-xl font-bold mb-2">{ti(card, "title")}</h3>
                        <p className="text-ruzy-text text-sm">{ti(card, "description")}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </ScrollReveal>
            );
          }

          // 3. GRID SECTION
          if (section.__component === "sections.grid-section") {
            const gridImg = getStrapiImg(section.image);
            return (
              <ScrollReveal key={idx} delay={0.2}>
                <section className="mb-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div className="order-2 lg:order-1">
                    {gridImg && (
                      <img src={gridImg} alt="Varlık Yönetimi" className="rounded-3xl shadow-xl" />
                    )}
                  </div>
                  <div className="order-1 lg:order-2 space-y-8">
                    <h2 className="text-3xl md:text-4xl font-black">{t(section, "title")}</h2>
                    <p className="text-ruzy-text text-lg">{t(section, "description")}</p>
                    <div className="space-y-6">
                      {(section.features || []).map((f, i) => (
                        <div key={i} className="flex gap-4">
                          <div className="w-12 h-12 rounded-xl bg-ruzy-accent/10 text-ruzy-accent flex items-center justify-center flex-shrink-0">
                            <span className="material-symbols-outlined">{f.icon}</span>
                          </div>
                          <div>
                            <h4 className="font-bold text-lg">{ti(f, "title")}</h4>
                            <p className="text-ruzy-text text-sm">{ti(f, "description")}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              </ScrollReveal>
            );
          }

          // 4. CTA SECTION
          if (section.__component === "sections.cta") {
            return (
              <ScrollReveal key={idx}>
                <section className="bg-ruzy-accent rounded-3xl p-12 text-center text-white shadow-2xl">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">{t(section, "Title")}</h2>
                  <p className="mb-10 text-white/80 max-w-xl mx-auto">{t(section, "Description")}</p>
                  <button onClick={() => navigate("/iletisim")} className="bg-white text-ruzy-accent px-10 py-4 rounded-xl font-black hover:bg-ruzy-light transition-colors">
                    {lang === "en" ? "Contact Us" : "Bizimle İletişime Geçin"}
                  </button>
                </section>
              </ScrollReveal>
            );
          }

          return null;
        })}
      </main>
    </div>
  );
};

export default Tesisler;